import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { MessageSquareWarning, Search, Filter, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './Complaints.module.css';

const Complaints = () => {
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/complaints');
      setComplaints(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    if(!selectedComplaint) return;
    try {
      const { data } = await api.put(`/admin/complaints/${selectedComplaint._id}/status`, { status });
      setComplaints(complaints.map(c => c._id === selectedComplaint._id ? data : c));
      setSelectedComplaint(null);
      toast.success('Status updated successfully');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const getUrgencyBadge = (urgency) => {
    const classes = {
      'High': styles.urgencyHigh,
      'Medium': styles.urgencyMedium,
      'Low': styles.urgencyLow
    };
    return <span className={`${styles.badgeUrgency} ${classes[urgency]}`}>{urgency}</span>;
  };

  const getStatusBadge = (status) => {
    const classes = {
      'Pending': styles.statusPending,
      'In Progress': styles.statusProgress,
      'Resolved': styles.statusResolved
    };
    return <span className={`${styles.badgeStatus} ${classes[status]}`}>{status}</span>;
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Complaints Helpdesk" 
        breadcrumb={['Society Management', 'Complaints']} 
      />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input type="text" placeholder="Search by ID or Unit..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Unit</th>
                <th>Issue Description</th>
                <th>Urgency</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="6">Loading...</td></tr> : complaints.map(complaint => (
                <tr key={complaint._id}>
                  <td><strong>{complaint._id.substring(complaint._id.length - 6).toUpperCase()}</strong></td>
                  <td>{complaint.resident?.wing}-{complaint.resident?.flatNumber}</td>
                  <td>
                    <div className={styles.issueDetails}>
                      <strong>{complaint.title}</strong>
                      <span>{complaint.category} • {new Date(complaint.createdAt).toLocaleDateString()}</span>
                    </div>
                  </td>
                  <td>{getUrgencyBadge(complaint.priority)}</td>
                  <td>{getStatusBadge(complaint.status === 'Open' ? 'Pending' : complaint.status)}</td>
                  <td>
                    <button className={styles.btnView} onClick={() => setSelectedComplaint(complaint)}>
                      Manage Ticket
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.mobileList}>
            {loading ? <div style={{padding: '20px', textAlign: 'center'}}>Loading...</div> : complaints.map(complaint => (
              <div key={complaint._id} className={styles.mobileCard}>
                <div className={styles.cardTop}>
                  <div className={styles.issueDetails}>
                    <strong>{complaint.title}</strong>
                    <span>{complaint.category} • {new Date(complaint.createdAt).toLocaleDateString()}</span>
                  </div>
                  {getUrgencyBadge(complaint.priority)}
                </div>
                <div className={styles.cardMid}>
                  <p><strong>Ticket ID:</strong> {complaint._id.substring(complaint._id.length - 6).toUpperCase()}</p>
                  <p><strong>Unit:</strong> {complaint.resident?.wing}-{complaint.resident?.flatNumber}</p>
                </div>
                <div className={styles.cardBottom}>
                  {getStatusBadge(complaint.status === 'Open' ? 'Pending' : complaint.status)}
                  <button className={styles.btnView} onClick={() => setSelectedComplaint(complaint)}>
                    Manage Ticket
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Drawer 
        isOpen={!!selectedComplaint} 
        onClose={() => setSelectedComplaint(null)} 
        title={`Ticket ${selectedComplaint?._id?.substring(selectedComplaint._id.length - 6).toUpperCase()}`}
      >
        {selectedComplaint && (
          <div className={styles.ticketDetail}>
            <div className={styles.ticketMeta}>
              <div>
                <span>Raised by Unit</span>
                <strong>{selectedComplaint.resident?.wing}-{selectedComplaint.resident?.flatNumber}</strong>
              </div>
              <div>
                <span>Category</span>
                <strong>{selectedComplaint.category}</strong>
              </div>
              <div>
                <span>Date Logged</span>
                <strong>{new Date(selectedComplaint.createdAt).toLocaleDateString()}</strong>
              </div>
            </div>

            <div className={styles.ticketContent}>
              <h3>{selectedComplaint.title}</h3>
              <p>{selectedComplaint.description}</p>
            </div>

            <div className={styles.formGroup}>
              <label>Add Resolution Note</label>
              <textarea className={styles.textarea} rows="3" placeholder="Type your response to the resident here..."></textarea>
            </div>

            <div className={styles.updateSection}>
              <h3>Update Status</h3>
              <div className={styles.statusButtons}>
                <button 
                  className={styles.btnUpdateProgress} 
                  onClick={() => handleStatusChange('In Progress')}
                  disabled={selectedComplaint.status === 'In Progress'}
                >
                  <Clock size={16} /> Mark In Progress
                </button>
                <button 
                  className={styles.btnUpdateResolved} 
                  onClick={() => handleStatusChange('Resolved')}
                  disabled={selectedComplaint.status === 'Resolved'}
                >
                  <CheckCircle size={16} /> Mark Resolved
                </button>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default Complaints;
